import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISSED = "fra_install_dismissed";
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

/** Dismissible "add to home screen" bar. Chrome uses beforeinstallprompt; iOS gets manual steps. */
export const InstallPrompt = () => {
  const [prompt, setPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISSED) || standalone()) return;

    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari never fires beforeinstallprompt, so offer the manual route instead.
    let timer;
    if (isIos()) timer = setTimeout(() => setShow(true), 4000);

    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); clearTimeout(timer); };
  }, []);

  const dismiss = () => { localStorage.setItem(DISMISSED, "1"); setShow(false); };

  const install = async () => {
    if (!prompt) { setIosHelp(true); return; }
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") dismiss(); else setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed inset-x-3 bottom-3 z-50 rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_18px_40px_-16px_rgba(15,23,42,0.4)] md:left-auto md:right-4 md:w-80"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-700 text-white">
          <Download className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-semibold text-slate-900">Keep recall alerts one tap away</p>
          {iosHelp ? (
            <p className="mt-1 text-xs leading-relaxed text-slate-600" data-testid="install-ios-help">
              Tap <Share className="inline h-3.5 w-3.5 align-[-2px]" /> Share in Safari, then choose
              <strong> Add to Home Screen</strong>.
            </p>
          ) : (
            <p className="mt-1 text-xs leading-relaxed text-slate-600">
              Add Food Recall Alerts to your home screen - it opens like an app, no store needed.
            </p>
          )}
          {!iosHelp && (
            <button
              data-testid="install-app-btn"
              onClick={install}
              className="mt-3 w-full rounded-full bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
            >
              {isIos() ? "Show me how" : "Install app"}
            </button>
          )}
        </div>
        <button
          data-testid="install-dismiss-btn"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="-mr-1 -mt-1 rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
